import { PlayerManager } from 'chromecast-caf-receiver/cast.framework';
import { EventType } from 'chromecast-caf-receiver/cast.framework.events';
import {
  PlayerState,
  StreamType,
} from 'chromecast-caf-receiver/cast.framework.messages';
import type { CastSession } from '../types/session';
import type { CastMedia } from '../types/media';
import { Hermes } from '../network';
import { Logger } from '../logger';
import { prefetchAndQueueNext } from './queue-manager';

/**
 * Checkpoint — periodically reports playback progress to Hermes.
 *
 * Ported from `legacy/js/receiver.js` (lines 510-537). Responsible for:
 * - Sending `UpdateCheckpoint` mutation to Hermes every 60 seconds
 * - Bypassing for LIVE streams and when `currentTime > duration`
 * - Only sending when `PlayerState === PLAYING`
 * - Cleaning up the interval on ENDED, ERROR, and state transitions
 *   to IDLE/STANDBY (preventing ghost sends and memory leaks)
 *
 * **Differences from legacy:**
 * - Uses `setInterval` (60s) instead of polling every `TIME_UPDATE` event
 * - Uses typed Hermes SDK (`Hermes.client.UpdateCheckpoint`) instead of XHR
 * - Reads mediaId/playlistId from `CastMedia` state (populated by load-interceptor)
 * - Explicit cleanup on ENDED, ERROR events and state transitions
 */

const LOG_TAG = 'CHECKPOINT';

/** Interval in milliseconds between checkpoint sends. */
const CHECKPOINT_INTERVAL_MS = 60_000;

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

/** Active interval ID, or `null` if no checkpoint loop is running. */
let intervalId: ReturnType<typeof setInterval> | null = null;

// ---------------------------------------------------------------------------
// Core checkpoint logic
// ---------------------------------------------------------------------------

/**
 * Sends a single checkpoint to Hermes if conditions are met:
 * - Player is in PLAYING state
 * - Stream is not LIVE
 * - currentTime is valid (> 0 and <= duration)
 * - Media has a valid contentId and playlistId
 */
/** Threshold in seconds — when remaining time is below this, prefetch next. */
const PREFETCH_THRESHOLD_SEC = 60;

function sendCheckpoint(
  playerManager: PlayerManager,
  media: CastMedia,
  session: CastSession,
): void {
  // Only send while actively playing
  if (playerManager.getPlayerState() !== PlayerState.PLAYING) {
    return;
  }

  // Get current media info from the player
  const mediaInfo = playerManager.getMediaInformation();
  if (!mediaInfo) return;

  // Bypass for LIVE streams
  if (mediaInfo.streamType === StreamType.LIVE) {
    return;
  }

  // Bypass when no media IDs
  if (!media.contentId || !media.playlistType) {
    return;
  }

  const currentTime = Math.floor(playerManager.getCurrentTimeSec());
  const duration = mediaInfo.duration ?? 0;

  // Bypass when currentTime exceeds duration (credits / post-roll)
  if (duration > 0 && currentTime > duration) {
    return;
  }

  // Bypass when time is not yet valid
  if (currentTime <= 0) {
    return;
  }

  Logger.debug(LOG_TAG, 'Sending checkpoint', {
    mediaId: media.contentId,
    currentTime,
    duration,
  });

  // Extract mediaId from metadata.customData if available,
  // otherwise use contentId from CastMedia
  const metadata = mediaInfo.metadata as
    | { customData?: { mediaId?: string; playlistId?: string } }
    | undefined;
  const mediaId = metadata?.customData?.mediaId ?? media.contentId;
  const playlistId = metadata?.customData?.playlistId ?? '';

  if (!playlistId) {
    Logger.warn(LOG_TAG, 'No playlistId available — skipping checkpoint');
    return;
  }

  Hermes.client
    .UpdateCheckpoint({
      input: {
        media: mediaId,
        playlist: playlistId,
        checkpoint: currentTime,
      },
    })
    .then(() => {
      Logger.debug(LOG_TAG, 'Checkpoint sent', { currentTime });
    })
    .catch((err: unknown) => {
      Logger.error(LOG_TAG, 'Failed to send checkpoint', err);
    });

  // Prefetch next episode when nearing the end
  const remaining = duration - currentTime;
  if (remaining > 0 && remaining <= PREFETCH_THRESHOLD_SEC) {
    prefetchAndQueueNext(playerManager, session);
  }
}

// ---------------------------------------------------------------------------
// Start / Stop
// ---------------------------------------------------------------------------

/**
 * Starts the checkpoint interval. If an interval is already running,
 * it is cleared first to avoid duplicates.
 */
function startCheckpointInterval(
  playerManager: PlayerManager,
  media: CastMedia,
  session: CastSession,
): void {
  stopCheckpointInterval();

  intervalId = setInterval(() => {
    sendCheckpoint(playerManager, media, session);
  }, CHECKPOINT_INTERVAL_MS);

  Logger.debug(LOG_TAG, 'Checkpoint interval started');
}

/**
 * Stops the checkpoint interval if one is running.
 * Safe to call multiple times.
 */
export function stopCheckpointInterval(): void {
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
    Logger.debug(LOG_TAG, 'Checkpoint interval stopped');
  }
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

/**
 * Registers the checkpoint system on the given player manager.
 *
 * - Starts the interval when media begins playing (`PLAYING` event)
 * - Stops the interval on `ENDED`, `ERROR`, `PAUSE`, and state transitions
 * - Restarts on `PLAY` (resume after pause)
 *
 * @param playerManager - Cast player manager instance
 * @param session       - Session state (not directly used but available for future enrichment)
 * @param media         - Media state (provides contentId/playlistType for checkpoint)
 */
export function registerCheckpoint(
  playerManager: PlayerManager,
  session: CastSession,
  media: CastMedia,
): void {
  // ---------------------------------------------------------------
  // Start checkpoint on PLAYING
  // ---------------------------------------------------------------
  playerManager.addEventListener(EventType.PLAYING, () => {
    Logger.debug(LOG_TAG, 'PLAYING — starting checkpoint interval');
    startCheckpointInterval(playerManager, media, session);
  });

  // ---------------------------------------------------------------
  // Also start on PLAY (resume from pause)
  // ---------------------------------------------------------------
  playerManager.addEventListener(EventType.PLAY, () => {
    if (playerManager.getPlayerState() === PlayerState.PLAYING) {
      startCheckpointInterval(playerManager, media, session);
    }
  });

  // ---------------------------------------------------------------
  // Stop checkpoint on PAUSE (no need to send while paused)
  // ---------------------------------------------------------------
  playerManager.addEventListener(EventType.PAUSE, () => {
    Logger.debug(LOG_TAG, 'PAUSE — stopping checkpoint interval');
    stopCheckpointInterval();
  });

  // ---------------------------------------------------------------
  // Stop checkpoint on ENDED (playback complete)
  // ---------------------------------------------------------------
  playerManager.addEventListener(EventType.ENDED, () => {
    Logger.debug(LOG_TAG, 'ENDED — stopping checkpoint interval');
    stopCheckpointInterval();
  });

  // ---------------------------------------------------------------
  // Stop checkpoint on ERROR (playback failed)
  // ---------------------------------------------------------------
  playerManager.addEventListener(EventType.ERROR, () => {
    Logger.debug(LOG_TAG, 'ERROR — stopping checkpoint interval');
    stopCheckpointInterval();
  });

  // ---------------------------------------------------------------
  // Stop checkpoint on ABORT (media unloaded)
  // ---------------------------------------------------------------
  playerManager.addEventListener(EventType.ABORT, () => {
    Logger.debug(LOG_TAG, 'ABORT — stopping checkpoint interval');
    stopCheckpointInterval();
  });
}
